import {Autocomplete, ListItemText} from "@mui/material";
import debounce from "@mui/utils/debounce";
import {TextField} from "@variocube/app-ui";
import React, {useMemo, useState} from "react";
import {useAsync} from "react-async-hook";
import {useUserApi} from "../api";
import {useLocalization} from "../i18n";

interface UserSelectProps {
	label?: string;
	required?: boolean;
	value: number | undefined;
	onChange: (value: number | undefined) => void;
	fullWidth?: boolean;
	disabled?: boolean;
	worksAt?: number;
}

export function UserSelect(props: Readonly<UserSelectProps>) {
	const {label, required, value, onChange, fullWidth, disabled, worksAt} = props;
	const {t} = useLocalization();

	const {queryUsers} = useUserApi();

	const [search, setSearch] = useState("");

	const setSearchDebounced = debounce(setSearch, 500);

	const query = useMemo(() => ({
		worksAt,
		search,
		size: 100,
	}), [worksAt, search]);
	const {loading, result} = useAsync(queryUsers, [query]);

	const users = result?.content ?? [];

	return (
		<Autocomplete
			renderInput={(parameters) => (
				<TextField placeholder={t("hint.select")} required={required} label={label} {...parameters} />
			)}
			onInputChange={(_, value) => setSearchDebounced(value)}
			fullWidth={fullWidth}
			disabled={disabled}
			loading={loading}
			options={users ?? []}
			getOptionLabel={option => typeof option == "string" ? option : (option.displayName ?? "")}
			value={users?.find(creditor => creditor.id == value) ?? null}
			getOptionKey={option => typeof option == "string" ? option : option.id}
			onChange={(_, v) => {
				if (typeof v == "string") {
					setSearch(v);
				}
				else {
					onChange(v?.id ?? undefined);
				}
			}}
			renderOption={(props, user) => (
				<li {...props}>
					<ListItemText primary={user.displayName} secondary={user.email} />
				</li>
			)}
			freeSolo
			autoSelect
			autoHighlight
		/>
	);
}
